import { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { ReemaIconComponent } from '../../display/Svg/ReemaIconComponent';
import { useTheme } from '@mui/material/styles';

interface GradientLoaderProps {
  backgroundColor?: string;
  gradientColors?: string[];
  showIcon?: boolean;
  loadingText?: string;
}

export const GradientLoader = ({
  backgroundColor = '#f5f5f5',
  gradientColors,
  showIcon = false,
  loadingText,
}: GradientLoaderProps) => {
  const theme = useTheme();
  const defaultGradientColors = [
    theme.palette.peachShades[300],
    theme.palette.navyShades[900],
  ];
  const gradColors = gradientColors || defaultGradientColors;

  const gradient = useMemo(() => {
    return `linear-gradient(${gradColors.join(', ')})`;
  }, [gradColors]);

  return (
    <Box
      sx={{
        padding: '48px',
        display: 'grid',
        placeItems: 'center',
        height: '100%',
        width: loadingText ? 'auto' : '100%',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          height: '200px',
          width: '200px',
          display: 'grid',
          placeItems: 'center',
        }}
      >
        {/* Rotating gradient layers */}
        <Box
          sx={{
            position: 'absolute',
            height: '100%',
            width: '100%',
            borderRadius: '50%',
            '@keyframes animateGradient': {
              '0%': {
                transform: 'rotate(0deg)',
              },
              '100%': {
                transform: 'rotate(360deg)',
              },
            },
            animation: 'animateGradient 1.0s linear infinite',
            '& > .gradient-span': {
              position: 'absolute',
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              background: gradient,
            },
            '& > .gradient-span:nth-of-type(1)': {
              filter: 'blur(10px)',
            },
            '& > .gradient-span:nth-of-type(2)': {
              filter: 'blur(15px)',
            },
            '& > .gradient-span:nth-of-type(3)': {
              filter: 'blur(20px)',
            },
            '& > .gradient-span:nth-of-type(4)': {
              filter: 'blur(25px)',
            },
          }}
        >
          <Box className="gradient-span" />
          <Box className="gradient-span" />
          <Box className="gradient-span" />
          <Box className="gradient-span" />
        </Box>

        {/* Stationary center circle with icon */}
        <Box
          sx={{
            position: 'relative',
            height: '100%',
            width: '100%',
            borderRadius: '50%',
            backgroundColor: backgroundColor,
            zIndex: 1,
            display: 'grid',
            placeItems: 'center',
          }}
        >
          {showIcon && (
            <ReemaIconComponent
              width="90%"
              height="90%"
              sx={{
                '@keyframes animateScale': {
                  '0%': {
                    transform: 'scale(1)',
                    opacity: 1,
                  },
                  '50%': {
                    transform: 'scale(0.75)',
                    opacity: 0.75,
                  },
                  '100%': {
                    transform: 'scale(1)',
                    opacity: 1,
                  },
                },
                animation: 'animateScale 2s linear infinite',
              }}
            />
          )}
        </Box>
      </Box>
      {loadingText && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            height: '100%',
          }}
        >
          <Typography
            variant="titleLarge"
            color="surfaceContainer.contrastText"
            fontWeight="semibold"
          >
            {loadingText}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
